import { api, tokenStore } from "./client";
import type { TokenPair, User, UserSubscriptions } from "@/types";

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  username: string;
  email: string;
  full_name?: string;
  phone?: string;
  password: string;
}

export async function login(payload: LoginPayload): Promise<TokenPair> {
  // SimpleJWT expects USERNAME_FIELD (email in our config).
  const { data } = await api.post<TokenPair>("/auth/token/", payload, {
    // Skip refresh interceptor for login itself
    headers: { Authorization: "" },
  });
  tokenStore.set(data.access, data.refresh);
  return data;
}

export async function register(payload: RegisterPayload): Promise<User> {
  const { data } = await api.post<User>("/register/", payload);
  return data;
}

export function logout() {
  tokenStore.clear();
}

export async function fetchCurrentUser(): Promise<User> {
  const { data } = await api.get<User>("/users/me/");
  return data;
}

export async function listUsers(): Promise<User[]> {
  const { data } = await api.get<{ results: User[] } | User[]>("/users/");
  return Array.isArray(data) ? data : data.results;
}

export async function updateMySubscriptions(
  payload: Partial<UserSubscriptions>,
): Promise<UserSubscriptions> {
  const { data } = await api.patch<UserSubscriptions>("/users/me/subscriptions/", payload);
  return data;
}

export async function setUserSubscriptions(
  userId: number,
  payload: Partial<UserSubscriptions>,
): Promise<UserSubscriptions> {
  const { data } = await api.patch<UserSubscriptions>(
    `/users/${userId}/set-subscriptions/`,
    payload,
  );
  return data;
}

// ─── Telegram account linking ────────────────────────────────────────────────

export interface TelegramLink {
  bot_username: string;
  deep_link: string;
  code: string;
  expires_at: string;
}

export async function createTelegramLink(): Promise<TelegramLink> {
  const { data } = await api.post<TelegramLink>("/users/me/telegram-link/");
  return data;
}

export async function unlinkTelegram(): Promise<UserSubscriptions> {
  const { data } = await api.delete<UserSubscriptions>("/users/me/telegram-link/");
  return data;
}

export async function checkTelegramLink(): Promise<{ linked: boolean; telegram_username: string }> {
  const { data } = await api.post("/users/me/telegram-link/check/");
  return data;
}

/** PNG QR of the pending deep link, as an object URL for an <img>. */
export async function fetchTelegramLinkQr(): Promise<string> {
  const { data } = await api.get("/users/me/telegram-link/qr/", { responseType: "blob" });
  return URL.createObjectURL(data);
}

export async function changePassword(
  current_password: string,
  new_password: string,
): Promise<void> {
  await api.post("/users/me/change-password/", {
    current_password,
    new_password,
  });
}

export interface ImpersonationResponse {
  access: string;
  refresh: string;
  user: User;
  expires_at: string;
  impersonator_email: string;
}

export async function impersonateUser(userId: number): Promise<ImpersonationResponse> {
  const { data } = await api.post<ImpersonationResponse>(`/users/${userId}/impersonate/`);
  return data;
}
